import { Paperclip, Send } from 'lucide-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AvatarCharecter from './AvatarCharecter'
import { getMentionText, repareURL } from 'utils'
import { LargeModal } from 'components/Modals';
import { Controller, useForm } from 'react-hook-form';
import ImageUploadWithPreview from './ImageUploadWithPreview';
import Zoom from 'react-medium-image-zoom';
import { AvatarImage } from 'components';

export default function InputMentions({ members, onSubmit }) {

    const textareaRef = useRef(null);
    const highlightRef = useRef(null);
    const prevRef = useRef({ userMentions: [], files: null }).current;

    const [content, setContent] = useState('');
    const [files, setFiles] = useState(null);
    const [isShow, setIsShow] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [mentionPosition, setMentionPosition] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

    const users = useMemo(() => members ? members.map(u => prevRef.userMentions.includes(u.email) ? null : u) : [], [members, prevRef.userMentions.length]);

    const handleTextChange = (e) => {
        const value = e.target.value;
        if (value.length) { prevRef.userMentions = [] }
        const cursorPosition = e.target.selectionStart;
        setContent(value);
        const textBeforeCursor = value.substring(0, cursorPosition);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
                setSearchTerm(textAfterAt.toLowerCase());
                setMentionPosition(lastAtIndex);
                const filteredUsers = users.filter(user =>
                    user && (`${user?.email}`.toLowerCase().includes(textAfterAt.toLowerCase()))
                    || user?.name?.toLowerCase().includes(textAfterAt.toLowerCase())
                );
                setSuggestions(filteredUsers);
                setShowSuggestions(true);
            } else {
                setShowSuggestions(false);
            }
        } else {
            setShowSuggestions(false);
        }
    };

    const handleSuggestionClick = (user) => {
        prevRef.userMentions.push(user?.email);
        const beforeMention = content.substring(0, mentionPosition);
        const afterMention = content.substring(mentionPosition + searchTerm.length + 1);

        const mentionText = user.email.split("@")[0];
        const newValue = beforeMention + `@${mentionText}` + afterMention;

        setContent(newValue);
        setShowSuggestions(false);

        setTimeout(() => {
            textareaRef.current?.focus();
            const newPosition = beforeMention.length + mentionText.length + 1;
            textareaRef.current?.setSelectionRange(newPosition, newPosition);
        }, 0);
    };

    const handleScroll = () => {
        if (highlightRef.current && textareaRef.current) {
            highlightRef.current.scrollTop = textareaRef.current.scrollTop;
            highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
        }
    };
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.addEventListener('scroll', handleScroll);
            return () => textarea.removeEventListener('scroll', handleScroll);
        }
    }, []);

    const onClose = () => {
        setIsShow(false)
    }

    const onConfirmAttachment = ({ files }: { files: FileList }) => {
        console.log("🚀 ~ onConfirmAttachment ~ files:", files)
        if (files?.length) {
            prevRef.files = files;
            for (let i = 0; i < files.length; ++i) {
                const file = files[i];
                const reader = new FileReader();
                reader.onloadend = () => setFiles(s => (s ? [...s, reader.result as string] : [reader.result as string]));
                reader.readAsDataURL(file);
            }
        }
        onClose();
    }

    const handleSubmit = () => {
        onSubmit({ files: prevRef.files, content }, () => {
            prevRef.files = null;
            prevRef.userMentions = [];
            setFiles(null);
            setContent('');
        });
    }

    return (
        <div className="my-2 border-b pt-4 border-gray-200 py-4">
            <div className="relative">
                <div
                    ref={highlightRef}
                    className="absolute inset-0 p-3 text-base leading-6 pointer-events-none overflow-hidden whitespace-pre-wrap break-words border rounded border-gray-300 text-gray-900"
                    style={{
                        fontSize: '14px',
                        lineHeight: '1.5',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                    }}
                >
                    {getMentionText(content, members)}
                </div>
                {/* <div
                        className="absolute top-0 left-0 w-full h-full px-3 py-3 whitespace-pre-wrap pointer-events-none border rounded border-gray-300"
                        dangerouslySetInnerHTML={{ __html: renderHighlightedText(`${comment}`) }}
                    /> */}
                <textarea
                    ref={textareaRef}
                    value={content}
                    onChange={(e) => handleTextChange(e)}
                    placeholder="Nhập bình luận..."
                    className="relative w-full p-3 bg-transparent outline-none text-gray-900 focus:outline-none resize-none "
                    style={{
                        fontSize: '14px',
                        lineHeight: '1.5',
                        color: 'transparent',
                        caretColor: 'blue',
                    }}
                    rows={4}
                />
                {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute left-0 w-[300px] top-12 bg-white border border-gray-100 rounded-lg shadow-lg z-10 max-h-56">
                        <div className='w-300'>
                            {suggestions.map((user) => (
                                <div
                                    key={user.id}
                                    onClick={() => handleSuggestionClick(user)}
                                    className="flex items-center p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                >
                                    <span className="text-2xl mr-3">
                                        {user?.avatar ? <AvatarImage className="w-6 h-6 rounded-full" src={repareURL(user?.avatar)} alt={user?.name} /> :
                                            <AvatarCharecter className="w-6 h-6" name={user?.name} />}
                                    </span>
                                    <div>
                                        <div className="font-medium text-gray-900">{user?.name}</div>
                                        <div className="text-sm text-gray-500">@{user?.email}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                    <button onClick={() => setIsShow(s => !s)} className="text-gray-400 hover:text-gray-600 flex items-center">
                        <Paperclip size={16} /> {files?.length && `(${files?.length})`}
                    </button>
                    {!!files?.length && files.map((e: any) => <Zoom key={`img-${e}`}><img className="w-6 h-6 rounded-full" src={e} alt={'tem'} /></Zoom>)}
                </div>
                <button onClick={handleSubmit} className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm flex items-center">
                    <Send size={16} className="mr-2" />
                    Gửi
                </button>
            </div>
            {isShow && <LargeModal title={`Đính kèm tệp`} onClose={onClose} >
                <Attachment onClose={onClose} onConfirm={e => {
                    onConfirmAttachment(e)
                }} />
            </LargeModal>}
        </div>
    )
}

interface IItemPopup {
    onClose?: () => void;
    onConfirm?: (a: any) => void;
}
export function Attachment({ onClose, onConfirm }: IItemPopup) {
    const {
        control,
        handleSubmit,
    } = useForm({ defaultValues: { files: null } });

    return (
        <div className="mx-auto bg-white rounded-lg ">
            <div className="content-modal p-4">
                <div>
                    <Controller
                        control={control}
                        name="files"
                        render={({ field }) => (
                            <ImageUploadWithPreview multiple={true} {...field} onPick={f => field.onChange(f)} />
                        )}
                    />

                </div>
            </div>
            <div className="flex justify-center gap-2 p-4">
                <button
                    onClick={onClose}
                    className="bg-gray-500 text-white px-4 py-1 rounded hover:bg-gray-600"
                >
                    Huỷ
                </button>
                {!!onConfirm && <button
                    onClick={() => handleSubmit(onConfirm)()}
                    className="bg-green-500 text-white px-4 py-1 rounded hover:bg-green-600"
                >
                    Xác nhận
                </button>}
            </div>
        </div>
    );
}
